import { z } from 'zod'
import {
  BasePaginateParamsSchema,
  BasePaginateResultSchema,
} from '@/api/common.ts'
import { QcHoldType } from '../qc8010/list.schema'

export const Qc8030SearchParamsSchema = z
  .object({
    number: z.string().optional(),
    itemId: z.string().optional(),
    dateFrom: z.coerce.date().optional(),
    dateTo: z.coerce.date().optional(),
    status: z
      .union([z.literal('1'), z.literal('2'), z.literal('3'), z.literal('4')])
      .optional(),
  })
  .merge(BasePaginateParamsSchema)

export type Qc8030SearchParams = z.infer<typeof Qc8030SearchParamsSchema>

export const Qc8030SortingReworkResultSchema = z.object({
  qtyPassed: z.number(),
  qtyRejected: z.number(),
  date: z.coerce.date().nullable(),
  byName: z.string(),
  remark: z.string().optional(),
  shift: z.number().nullish(),
})

export type Qc8030SortingReworkResultModel = z.infer<
  typeof Qc8030SortingReworkResultSchema
>

export const Qc8030WorkResultSchema = z.object({
  id: z.number(),
  confirmSortingResult: z.object({
    pass: z.boolean(),
    date: z.coerce.date().nullable(),
  }),
  sortingReworkResult: Qc8030SortingReworkResultSchema,
})

export type Qc8030WorkResultModel = z.infer<typeof Qc8030WorkResultSchema>

export const Qc8030MovementDocumentSchema = z.object({
  holdId: z.string(),
  movementDocumentId: z.number(),
  movementDocumentNumber: z.string(),
  qty: z.number(),
  lifecycleStatus: z.number(),
  statusUpdatedAt: z.coerce.date().nullable(),
  plannedDate: z.coerce.date().nullable(),
  remark: z.string().nullable(),
  isActive: z.boolean(),
  isReceived: z.boolean(),
  isCancelled: z.boolean(),
  id: z.number(),
  createdBy: z.number(),
  updatedBy: z.number().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type Qc8030MovementDocumentModel = z.infer<
  typeof Qc8030MovementDocumentSchema
>

export const Qc8030SearchResultModelSchema = z
  .object({
    id: z.string(),
    number: z.string(),
    date: z.coerce.date(),
    item: z.object({
      itemId: z.number(),
      lotNo: z.string(),
      lotDate: z.coerce.date(),
      mold: z.string(),
      unit: z.string(),
    }),
    lotDate: z.coerce.date().nullable(),
    problem: z.object({
      isRawMaterialProblem: z.boolean(),
      isComponentProblem: z.boolean(),
      appearances: z.array(z.string()).optional(),
      functionalities: z.array(z.string()).optional(),
      appearance: z.string(),
      functionality: z.string(),
      note: z.string().optional(),
    }),
    isClaimItem: z.boolean(),
    sortingReworkResult: Qc8030SortingReworkResultSchema,
    isConfirmSortingResultSet: z.boolean(),
    isNeedSortingReworkResult: z.boolean(),
    qty: z.number(),
    qtySortingRework: z.number(),
    type: z.nativeEnum(QcHoldType),
    createdAt: z.coerce.date(),
    status: z.number(),
    workResults: z.array(Qc8030WorkResultSchema),
    pickingNumber: z.string().nullish(),
    canCreateInv1140Document: z.boolean(),
    movementDocuments: z.array(Qc8030MovementDocumentSchema).default([]),
    remainingQuantity: z.number(),
  })
  .transform((v) => ({
    ...v,
    sumWorkResultQty: v.workResults
      .filter(
        (x) => !x.confirmSortingResult.date || x.confirmSortingResult.pass
      )
      .reduce(
        (a, b) =>
          a +
          b.sortingReworkResult.qtyPassed +
          b.sortingReworkResult.qtyRejected,
        0
      ),
  }))

export type Qc8030SearchResultModel = z.infer<
  typeof Qc8030SearchResultModelSchema
>

export const Qc8030SearchResultSchema = z
  .object({
    items: z.array(Qc8030SearchResultModelSchema),
  })
  .merge(BasePaginateResultSchema)

export type Qc8030SearchResult = z.infer<typeof Qc8030SearchResultSchema>
